import { useState, useEffect } from "react";
import { makeTr } from "@/i18n/tr";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Trophy, 
  Clock, 
  Coins,
  User,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  History,
  BarChart3,
  ChevronRight,
  Play,
  Download,
  RotateCcw, ShieldCheck, Shield } from "lucide-react";
import { Chessboard } from "react-chessboard";
import { Chess } from "chess.js";

import { API_URL } from '@/config/api'; // API_URL from config

export default function GameHistoryPage() {
  const { t, i18n } = useTranslation();
  const isRu = i18n.language === "ru";
  const tr = makeTr(i18n.language);
  const navigate = useNavigate();
  const { token, boardTheme } = useStore();
  
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [currentMove, setCurrentMove] = useState(0);
  const [analysisOpen, setAnalysisOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [historyRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/games/history?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/games/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (historyRes.ok) {
        const data = await historyRes.json();
        setGames(data.games || []);
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      console.error("Error loading game history:", error);
    }
    setLoading(false);
  };

  const loadAnalysis = async (gameId) => {
    try {
      const res = await fetch(`${API_URL}/api/games/${gameId}/analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalysisData(data);
        setCurrentMove(0);
        setAnalysisOpen(true);
      }
    } catch (error) {
      console.error("Error loading analysis:", error);
    }
  };

  const downloadPGN = () => {
    if (!analysisData?.pgn) return;
    
    const blob = new Blob([analysisData.pgn], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `teachess_${selectedGame?.id?.slice(0, 8)}.pgn`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    return `${mins} ${tr("мин", "min")}`;
  };

  const getResultIcon = (result) => {
    switch (result) {
      case "win": return <TrendingUp className="h-4 w-4 text-green-500" />;
      case "loss": return <TrendingDown className="h-4 w-4 text-red-500" />;
      case "draw": return <Minus className="h-4 w-4 text-yellow-500" />;
      default: return null;
    }
  };

  const getResultBadge = (result) => {
    const variants = {
      win: "bg-green-500/20 text-green-500",
      loss: "bg-red-500/20 text-red-500",
      draw: "bg-yellow-500/20 text-yellow-500"
    };
    const labels = {
      win: tr("Победа", "Win"),
      loss: tr("Поражение", "Loss"),
      draw: tr("Ничья", "Draw")
    };
    return (
      <Badge className={variants[result] || ""}>
        {labels[result] || result}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20" data-testid="game-history-page">
      <div>
        <h1 className="text-2xl font-black">{tr("История игр", "Game History")}</h1>
        <p className="text-sm text-muted-foreground">{tr("PvP матчи и статистика", "PvP matches and statistics")}</p>
      </div>

      <Tabs defaultValue="history">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="history">
            <History className="h-4 w-4 mr-2" />
            {tr("История", "History")}
          </TabsTrigger>
          <TabsTrigger value="stats">
            <BarChart3 className="h-4 w-4 mr-2" />
            {tr("Статистика", "Statistics")}
          </TabsTrigger>
        </TabsList>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          {games.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Trophy className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {tr("Нет завершённых игр", "No completed games")}
                </p>
                <Button className="mt-4" onClick={() => navigate("/lobby")}>
                  {tr("Найти игру", "Find Game")}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {games.map((game) => (
                <Card 
                  key={game.id} 
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => {
                    setSelectedGame(game);
                    loadAnalysis(game.id);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getResultIcon(game.result)}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{game.opponent || "Unknown"}</span>
                            {game.opponent_rating && (
                              <span className="text-xs text-muted-foreground">
                                ({game.opponent_rating})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(game.time_control || 600)}
                            </span>
                            {game.stake > 0 && (
                              <span className="flex items-center gap-1">
                                <Coins className="h-3 w-3" />
                                {game.stake} P
                              </span>
                            )}
                            <span>{game.moves_count} {tr("ходов", "moves")}</span>
                            {game.fairplay_status === "checked" && (
                              <span className="flex items-center gap-1 text-green-500" title={t("fairplay.checkedHint")} data-testid="fairplay-checked-badge">
                                <ShieldCheck className="h-3 w-3" />{t("fairplay.checked")}
                              </span>
                            )}
                            {game.fairplay_status === "analyzing" && (
                              <span className="flex items-center gap-1 text-yellow-500" data-testid="fairplay-analyzing-badge">
                                <Shield className="h-3 w-3" />{t("fairplay.analyzing")}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          {getResultBadge(game.result)}
                          {game.rating_change && (
                            <p className={`text-xs mt-1 ${game.rating_change > 0 ? "text-green-500" : "text-red-500"}`}>
                              {game.rating_change > 0 ? "+" : ""}{game.rating_change}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats" className="mt-4">
          {stats ? (
            <div className="space-y-4">
              {/* Overview */}
              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-black text-primary">{stats.total_games}</p>
                    <p className="text-xs text-muted-foreground">{tr("Всего игр", "Total Games")}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-black">{stats.win_rate}%</p>
                    <p className="text-xs text-muted-foreground">{tr("Винрейт", "Win Rate")}</p>
                  </CardContent>
                </Card>
              </div>

              {/* W/D/L */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-center">
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-green-500">{stats.wins}</p>
                      <p className="text-xs text-muted-foreground">{tr("Побед", "Wins")}</p>
                    </div>
                    <div className="text-center flex-1 border-x border-border">
                      <p className="text-2xl font-bold text-yellow-500">{stats.draws}</p>
                      <p className="text-xs text-muted-foreground">{tr("Ничьих", "Draws")}</p>
                    </div>
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-red-500">{stats.losses}</p>
                      <p className="text-xs text-muted-foreground">{tr("Поражений", "Losses")}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Money Stats */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{tr("Финансы", "Finances")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{tr("Выиграно", "Won")}</span>
                      <span className="text-green-500 font-semibold">+{stats.total_won}₽</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{tr("Проиграно", "Lost")}</span>
                      <span className="text-red-500 font-semibold">-{stats.total_lost}₽</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-semibold">{tr("Итого", "Net")}</span>
                      <span className={`font-bold ${stats.net_profit >= 0 ? "text-green-500" : "text-red-500"}`}>
                        {stats.net_profit >= 0 ? "+" : ""}{stats.net_profit}₽
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Rating */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-muted-foreground">{tr("Текущий рейтинг", "Current Rating")}</p>
                      <p className="text-2xl font-bold">{stats.current_rating}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{tr("Максимальный", "Highest")}</p>
                      <p className="text-2xl font-bold text-primary">{stats.highest_rating}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              {tr("Нет данных", "No data")}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Analysis Dialog */}
      <Dialog open={analysisOpen} onOpenChange={setAnalysisOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {tr("Анализ партии", "Game Analysis")}
            </DialogTitle>
          </DialogHeader>
          
          {analysisData && (
            <div className="space-y-4">
              {/* Board */}
              <div className="w-full aspect-square max-w-[300px] mx-auto">
                <Chessboard
                  position={analysisData.positions[currentMove]?.fen || "start"}
                  boardWidth={300}
                  arePiecesDraggable={false}
                />
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentMove(0)}
                  disabled={currentMove === 0}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentMove(Math.max(0, currentMove - 1))}
                  disabled={currentMove === 0}
                >
                  ◀
                </Button>
                <span className="text-sm px-4">
                  {currentMove} / {analysisData.positions.length - 1}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentMove(Math.min(analysisData.positions.length - 1, currentMove + 1))}
                  disabled={currentMove >= analysisData.positions.length - 1}
                >
                  ▶
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentMove(analysisData.positions.length - 1)}
                  disabled={currentMove >= analysisData.positions.length - 1}
                >
                  ⏭
                </Button>
              </div>

              {/* Move info */}
              {currentMove > 0 && (
                <div className="text-center text-sm">
                  <span className="font-mono bg-muted px-2 py-1 rounded">
                    {Math.ceil(currentMove / 2)}. {analysisData.positions[currentMove]?.san}
                  </span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={downloadPGN}>
                  <Download className="h-4 w-4 mr-2" />
                  PGN
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={() => {
                    // Request rematch
                    fetch(`${API_URL}/api/games/${selectedGame.id}/rematch`, {
                      method: "POST",
                      headers: { Authorization: `Bearer ${token}` }
                    }).then(res => res.json()).then(data => {
                      if (data.game_id) {
                        navigate(`/game/${data.game_id}`);
                      }
                    });
                  }}
                >
                  <Play className="h-4 w-4 mr-2" />
                  {tr("Реванш", "Rematch")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
